import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { User, Lock, Loader2 } from "lucide-react";

export default function Login() {
  const { login, user, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) nav("/");
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6 md:p-10 relative overflow-hidden">
      {/* Soft brand background wash */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-blue-50 blur-3xl opacity-70" />
        <div className="absolute -bottom-40 -right-40 h-[520px] w-[520px] rounded-full bg-blue-100 blur-3xl opacity-60" />
      </div>

      <div className="relative w-full max-w-6xl grid md:grid-cols-2 gap-10 md:gap-16 items-center">
        {/* Left — Logo & tagline */}
        <div className="flex flex-col items-center md:items-start text-center md:text-left">
          <img
            src="/logo_transparent.png"
            alt="Ratan Motors"
            data-testid="brand-logo"
            className="w-[300px] md:w-[420px] max-w-full h-auto drop-shadow-sm"
          />
          <div className="mt-6 h-1 w-32 bg-gradient-to-r from-blue-600 via-blue-500 to-transparent rounded-full" />
          <div className="mt-4 text-slate-700 font-semibold tracking-wide text-lg md:text-xl">
            Central Money Receipt Management System
          </div>
          <div className="mt-2 text-xs uppercase tracking-[0.2em] text-slate-400">
            Tata Motors Authorized CV Dealer · Jharkhand
          </div>
        </div>

        {/* Right — Login card */}
        <div className="w-full max-w-md mx-auto md:mx-0 md:ml-auto">
          <div className="rounded-2xl overflow-hidden shadow-2xl shadow-blue-200/60 bg-white border border-slate-200">
            {/* Blue header */}
            <div className="sidebar-gradient text-white px-8 py-8 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
              <div className="absolute -bottom-16 -left-8 h-32 w-32 rounded-full bg-blue-300/10 blur-2xl" />
              <h1 className="relative text-3xl font-extrabold tracking-tight">Employee Login</h1>
              <p className="relative text-blue-100 text-sm mt-1">Welcome to Ratan Motors ERP</p>
            </div>

            {/* Body */}
            <form onSubmit={submit} className="p-8 space-y-5">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">User ID</label>
                <div className="flex overflow-hidden rounded-xl border border-slate-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition">
                  <div className="w-12 shrink-0 bg-blue-600 text-white flex items-center justify-center">
                    <User className="h-4.5 w-4.5" />
                  </div>
                  <input
                    type="email"
                    data-testid="login-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="flex-1 px-4 py-3 outline-none text-sm text-slate-900 bg-white"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Password</label>
                <div className="flex overflow-hidden rounded-xl border border-slate-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition">
                  <div className="w-12 shrink-0 bg-blue-600 text-white flex items-center justify-center">
                    <Lock className="h-4.5 w-4.5" />
                  </div>
                  <input
                    type="password"
                    data-testid="login-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="flex-1 px-4 py-3 outline-none text-sm text-slate-900 bg-white"
                    required
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-600 select-none">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                Remember Me
              </label>

              {error && (
                <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
                  {error}
                </div>
              )}

              <button
                type="submit"
                data-testid="login-submit"
                disabled={busy}
                className="w-full py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold tracking-widest text-sm flex items-center justify-center gap-2 transition shadow-lg shadow-blue-600/25"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                SIGN IN
              </button>
            </form>
          </div>

          <div className="text-center mt-5 text-xs text-slate-400">
            © {new Date().getFullYear()} Ratan Motors · All rights reserved
          </div>
        </div>
      </div>
    </div>
  );
}
